import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { Pressable, Text, View, ViewStyle } from 'react-native'
import styled from 'styled-components/native'
import { Feather } from '../../../../components/icon/vectorIcons'
import { useDropzone } from 'react-dropzone'
import { ImageFields } from '../../../../doclets/types'
import {
  DocletImage,
  ImageHolder,
  useDocletImage
} from '../../../../doclets/blocks/image/ImageBlock'
import useTranslation from '../../../../hooks/useTranslation'
import uploadToCloudinary from '../../../../utils/uploadToCloudinary'
import { builderColors, builderRadii } from '../builderColors'

const CLOUDINARY_FOLDER = 'doclets'

interface CloudinaryUpload {
  public_id: string
  width?: number
  height?: number
}

export type UploadedImage = Pick<ImageFields, 'source' | 'width' | 'height'>

interface EditableImageProps {
  block: ImageHolder
  // Called when an upload finishes, with the new picture
  onUploaded: (image: UploadedImage) => void
  onFocus?: () => void
  selected?: boolean
  // Wraps the image and `children` once there is an image
  filledStyle?: ViewStyle
  // Shown under the image once there is one (the Image block's caption)
  children?: ReactNode
}

/**
 * The builder's picture, shared by the Image and Image + text editors: a drop
 * zone until an image is uploaded, then the image itself. Dropping another
 * file on it (or "Replace image" while it's selected) uploads a replacement.
 */
const EditableImage = ({
  block,
  onUploaded,
  onFocus,
  selected = false,
  filledStyle,
  children
}: EditableImageProps) => {
  const { t } = useTranslation()
  const image = useDocletImage(block)
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  // The upload finishes later: report it through the callback as it is then,
  // so the block merges into its props as they are then
  const latestOnUploaded = useRef(onUploaded)
  latestOnUploaded.current = onUploaded
  const mounted = useRef(true)
  useEffect(
    () => () => {
      mounted.current = false
    },
    []
  )

  // One upload at a time: a second file dropped (or chosen) mid-upload would
  // race the first, interleaving progress and saving whichever finished last.
  // A ref, not state, so two drops in the same tick can't both get through.
  const uploadInFlight = useRef(false)

  const upload = useCallback(
    async (files: File[]) => {
      const file = files[0]
      if (!file || uploadInFlight.current) return
      uploadInFlight.current = true
      onFocus?.()
      setError(null)
      setProgress(0)
      try {
        const result = (await uploadToCloudinary(
          file,
          CLOUDINARY_FOLDER,
          (percent: number) => mounted.current && setProgress(percent)
        )) as CloudinaryUpload
        latestOnUploaded.current({
          source: { publicId: result.public_id },
          width: result.width ?? null,
          height: result.height ?? null
        })
      } catch (err) {
        if (mounted.current) setError(t('doclets:image:uploadFailed'))
      } finally {
        uploadInFlight.current = false
        if (mounted.current) setProgress(null)
      }
    },
    [onFocus, t]
  )

  const uploading = progress !== null

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop: upload,
    accept: 'image/*',
    multiple: false,
    // No drops (or "Drop to upload" overlay) while an upload is running
    disabled: uploading,
    // Clicking selects the block; uploading is by drop or the buttons
    noClick: true,
    noKeyboard: true
  })

  // Typed loosely, as in FileDropzone: react-dropzone's prop types don't
  // match MI6's React DOM types
  const rootProps: any = getRootProps()
  const inputProps: any = getInputProps()

  return (
    <div {...rootProps} style={{ position: 'relative', outline: 'none' }}>
      <input {...inputProps} />
      {block.props.source ? (
        <View style={filledStyle}>
          <View>
            {image.uri ? (
              <DocletImage
                block={block}
                image={image}
                fallback={
                  <Unavailable>
                    <UnavailableText>
                      {t('doclets:image:couldNotLoad')}
                    </UnavailableText>
                  </Unavailable>
                }
              />
            ) : (
              // Builder only: the page itself just leaves the image out
              <Unavailable>
                <UnavailableText>
                  {t('doclets:image:cannotShow')}
                </UnavailableText>
              </Unavailable>
            )}
            {selected && !uploading && (
              <OverlayButton
                icon="refresh-cw"
                label={t('doclets:image:replace')}
                onPress={open}
                style={{ position: 'absolute', top: 8, left: 8 }}
              />
            )}
          </View>
          {children}
        </View>
      ) : (
        <EmptyZone>
          <Feather name="image" size={24} color={builderColors.mutedText} />
          <ZoneText>{t('doclets:image:dropHere')}</ZoneText>
          <OverlayButton
            icon="upload"
            label={t('doclets:image:choose')}
            onPress={() => {
              onFocus?.()
              open()
            }}
          />
        </EmptyZone>
      )}
      {(uploading || isDragActive) && (
        <Overlay>
          <OverlayText>
            {uploading
              ? t('doclets:image:uploading', { progress })
              : t('doclets:image:dropToUpload')}
          </OverlayText>
        </Overlay>
      )}
      {!!error && <ErrorText>{error}</ErrorText>}
    </div>
  )
}

interface OverlayButtonProps {
  icon: React.ComponentProps<typeof Feather>['name']
  label: string
  onPress: () => void
  style?: object
}

const OverlayButton = ({ icon, label, onPress, style }: OverlayButtonProps) => (
  <Pressable
    accessibilityRole="button"
    onPress={onPress}
    style={[
      {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'center',
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderWidth: 1,
        borderColor: builderColors.border,
        borderRadius: builderRadii.md,
        backgroundColor: '#FFFFFF'
      },
      style
    ]}
  >
    <Feather name={icon} size={14} color={builderColors.text} />
    <ButtonText>{label}</ButtonText>
  </Pressable>
)

const Unavailable = styled(View)`
  align-items: center;
  padding: 32px 16px;
  border-width: 1px;
  border-color: ${builderColors.border};
  border-radius: ${builderRadii.md}px;
  background-color: ${builderColors.canvas};
`

const UnavailableText = styled(Text)`
  font-size: 13px;
  color: ${builderColors.mutedText};
  text-align: center;
`

const EmptyZone = styled(View)`
  align-items: center;
  padding: 32px 16px;
  margin-bottom: 16px;
  border-width: 1px;
  border-style: dashed;
  border-color: ${builderColors.border};
  border-radius: ${builderRadii.md}px;
`

const ZoneText = styled(Text)`
  margin: 8px 0 12px;
  font-size: 13px;
  color: ${builderColors.mutedText};
`

const ButtonText = styled(Text)`
  margin-left: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${builderColors.text};
`

const Overlay = styled(View)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  align-items: center;
  justify-content: center;
  border-width: 2px;
  border-style: dashed;
  border-color: ${builderColors.selectedBorder};
  border-radius: ${builderRadii.md}px;
  background-color: rgba(240, 247, 255, 0.85);
`

const OverlayText = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: ${builderColors.tag};
`

const ErrorText = styled(Text)`
  margin-bottom: 16px;
  font-size: 12px;
  color: ${builderColors.danger};
`

export default EditableImage
